/** The studio: a teacher's own courses and lessons, their drafts, exercise schemas and previews. */
import { baseApi } from '../baseApi';
import type {
  ContentTree,
  CourseDraftOut,
  DraftCourse,
  DraftLesson,
  DraftTopic,
  EditorTemplates,
  ExerciseTypes,
  LessonDraftOut,
  NewCourseIn,
  PreviewIn,
  PreviewOut,
  Publication,
  PublicationIn,
  SaveOut,
  StandaloneLessonOut,
  StudioOut,
  TopicDraftOut,
} from '../types';
/**
 * Something inside a course: a topic or a lesson of it. The course is named by its
 * address (`author/course`), the topic or lesson by its name in the course: the last
 * part of its own address, which is what the editor's tree and the drafts call it.
 */
interface InCourse {
  courseId: string;
  id: string;
}

/** Where a lesson that stands alone (`author/-/lesson`) is edited. */
function standalone(address: string): string {
  const [author, , lesson] = address.split('/');
  return `/authoring/${author}/-/lessons/${lesson}`;
}

interface SaveArgs<Body> {
  body: Body;
  /** Check the draft and report problems without saving it. */
  dryRun: boolean;
}

/** A real save changes what learners see, the editor's tree and what is unlocked. */
const savedTags = (_result: unknown, error: unknown, { dryRun }: { dryRun: boolean }) =>
  error || dryRun ? [] : (['Content', 'Editor', 'Progress'] as const);

export const authoringApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getStudio: build.query<StudioOut, void>({
      query: () => '/authoring',
      providesTags: ['Editor'],
    }),
    // Starting points for new content, with a text field per given language.
    getEditorTemplates: build.query<EditorTemplates, string[]>({
      query: (locales) =>
        `/authoring/templates?${locales.map((locale) => `locales=${encodeURIComponent(locale)}`).join('&')}`,
    }),
    // The schema of every exercise type: what the editor builds its forms from.
    getExerciseTypes: build.query<ExerciseTypes, void>({
      query: () => '/authoring/exercise-types',
      keepUnusedDataFor: 3600,
    }),
    // An item of a draft as a learner would be shown it. Nothing is saved.
    previewItem: build.mutation<PreviewOut, PreviewIn>({
      query: (body) => ({ url: '/authoring/preview', method: 'POST', body }),
    }),
    createCourse: build.mutation<{ id: string }, NewCourseIn>({
      query: (body) => ({ url: '/authoring/courses', method: 'POST', body }),
      invalidatesTags: ['Editor'],
    }),
    getContentTree: build.query<ContentTree, string>({
      query: (courseId) => `/authoring/${courseId}`,
      providesTags: ['Editor'],
    }),
    publishCourse: build.mutation<Publication, { id: string; body: PublicationIn }>({
      query: ({ id, body }) => ({ url: `/authoring/${id}/publication`, method: 'PUT', body }),
      invalidatesTags: ['Editor', 'Content'],
    }),
    // Drafts are never served from the cache: an edit must start from the latest state.
    getCourseDraft: build.query<CourseDraftOut, string>({
      query: (courseId) => `/authoring/${courseId}/course`,
      providesTags: ['Editor'],
      keepUnusedDataFor: 0,
    }),
    getTopicDraft: build.query<TopicDraftOut, InCourse>({
      query: ({ courseId, id }) => `/authoring/${courseId}/topics/${id}`,
      providesTags: ['Editor'],
      keepUnusedDataFor: 0,
    }),
    getLessonDraft: build.query<LessonDraftOut, InCourse>({
      query: ({ courseId, id }) => `/authoring/${courseId}/lessons/${id}`,
      providesTags: ['Editor'],
      keepUnusedDataFor: 0,
    }),
    saveCourse: build.mutation<
      SaveOut,
      SaveArgs<{ baseRevision: string; course: DraftCourse }> & { courseId: string }
    >({
      query: ({ courseId, body, dryRun }) => ({
        url: `/authoring/${courseId}/course`,
        method: 'PUT',
        params: { dryRun },
        body,
      }),
      invalidatesTags: savedTags,
    }),
    // Something new is posted and named by the server, after its title: nobody types
    // an identifier. The answer carries the address it was given.
    createTopic: build.mutation<SaveOut, SaveArgs<{ topic: DraftTopic }> & { courseId: string }>({
      query: ({ courseId, body, dryRun }) => ({
        url: `/authoring/${courseId}/topics`,
        method: 'POST',
        params: { dryRun },
        body,
      }),
      invalidatesTags: savedTags,
    }),
    saveTopic: build.mutation<
      SaveOut,
      SaveArgs<{ baseRevision: string; topic: DraftTopic }> & InCourse
    >({
      query: ({ courseId, id, body, dryRun }) => ({
        url: `/authoring/${courseId}/topics/${id}`,
        method: 'PUT',
        params: { dryRun },
        body,
      }),
      invalidatesTags: savedTags,
    }),
    createLesson: build.mutation<
      SaveOut,
      SaveArgs<{ topicId: string; lesson: DraftLesson }> & { courseId: string }
    >({
      query: ({ courseId, body, dryRun }) => ({
        url: `/authoring/${courseId}/lessons`,
        method: 'POST',
        params: { dryRun },
        body,
      }),
      invalidatesTags: savedTags,
    }),
    saveLesson: build.mutation<
      SaveOut,
      SaveArgs<{ baseRevision: string; lesson: DraftLesson }> & InCourse
    >({
      query: ({ courseId, id, body, dryRun }) => ({
        url: `/authoring/${courseId}/lessons/${id}`,
        method: 'PUT',
        params: { dryRun },
        body,
      }),
      invalidatesTags: savedTags,
    }),
    // A lesson that stands alone, outside any course: `author/-/lesson`.
    getStandaloneLesson: build.query<StandaloneLessonOut, string>({
      query: (id) => standalone(id),
      providesTags: ['Editor'],
      keepUnusedDataFor: 0,
    }),
    createStandaloneLesson: build.mutation<
      SaveOut,
      SaveArgs<{ lesson: DraftLesson; locales: string[]; defaultLocale: string }>
    >({
      query: ({ body, dryRun }) => ({
        url: '/authoring/lessons',
        method: 'POST',
        params: { dryRun },
        body,
      }),
      invalidatesTags: savedTags,
    }),
    saveStandaloneLesson: build.mutation<
      SaveOut,
      SaveArgs<{ baseRevision: string; lesson: DraftLesson }> & { id: string }
    >({
      query: ({ id, body, dryRun }) => ({
        url: standalone(id),
        method: 'PUT',
        params: { dryRun },
        body,
      }),
      invalidatesTags: savedTags,
    }),
    publishLesson: build.mutation<Publication, { id: string; body: PublicationIn }>({
      query: ({ id, body }) => ({ url: `${standalone(id)}/publication`, method: 'PUT', body }),
      invalidatesTags: ['Editor', 'Content'],
    }),
  }),
});

export const {
  useGetExerciseTypesQuery,
  usePreviewItemMutation,
  useGetStudioQuery,
  useCreateCourseMutation,
  usePublishCourseMutation,
  usePublishLessonMutation,
  useGetStandaloneLessonQuery,
  useSaveStandaloneLessonMutation,
  useGetContentTreeQuery,
  useGetEditorTemplatesQuery,
  useGetCourseDraftQuery,
  useGetTopicDraftQuery,
  useGetLessonDraftQuery,
  useSaveCourseMutation,
  useCreateTopicMutation,
  useSaveTopicMutation,
  useCreateLessonMutation,
  useSaveLessonMutation,
  useCreateStandaloneLessonMutation,
} = authoringApi;
